import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  infiniteQueryOptions,
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from "@tanstack/react-query";

import { usePaginateQuery } from "@/hooks";
import { api } from "@/lib/api";
import { type Paginate, type SearchRequest, type User } from "@/types";
import { getNextPaginatePage } from "@/utils";

export type UserList = Paginate<User<true>>;

export const users = createQueryKeys("users", {
  all: (page?: number, filter?: SearchRequest) => [{ page, filter }],
  one: (id?: number) => [id],
  allInfinite: (filter?: SearchRequest) => [{ filter }],
});

const include = "position";

export const fetchUsersQueryOptions = (
  page: number = 0,
  search?: SearchRequest,
) =>
  queryOptions({
    ...users.all(page, search),
    queryFn: () =>
      api
        .get<UserList>(`/users`, {
          params: { page, include, ...search },
        })
        .then((response) => response.data),
  });

export const useFetchUsers = (search?: SearchRequest) =>
  usePaginateQuery((page) => fetchUsersQueryOptions(page, search));

export const fetchUserQueryOptions = (
  id?: number,
  includeDirectManagers = false,
) =>
  queryOptions({
    ...users.one(id),
    queryKey: [...users.one(id).queryKey, { includeDirectManagers }],
    queryFn: () =>
      api
        .get<
          User<true>
        >(`/users/${id}?include=${includeDirectManagers ? `${include},directManagerUsers` : include}`)
        .then((response) => response.data),
    enabled: id !== undefined,
  });

export const useFetchUser = (id?: number, includeDirectManagers = false) =>
  useQuery(fetchUserQueryOptions(id, includeDirectManagers));

export const fetchUsersInfiniteQueryOptions = (search?: SearchRequest) =>
  infiniteQueryOptions({
    ...users.allInfinite(search),
    queryFn: ({ pageParam }) =>
      api
        .get<UserList>(`/users`, {
          params: { page: pageParam, include, ...search },
        })
        .then((response) => response.data),
    initialPageParam: 1,
    getNextPageParam: getNextPaginatePage,
  });

export const useFetchUsersInfinite = (search?: SearchRequest) =>
  useInfiniteQuery({ ...fetchUsersInfiniteQueryOptions(search) });

export const useUpdateWebPushSubscription = () =>
  useMutation({
    mutationFn: (subscription: PushSubscription) => {
      const data = subscription.toJSON();
      return api.post("/me/web-push-subscription", {
        endpoint: data.endpoint,
        key: data.keys?.["p256dh"] ?? "",
        token: data.keys?.["auth"] ?? "",
      });
    },
  });
